import { Button, Field, FieldError, FieldLabel, toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import type { z } from "zod";
import type { ChartingCondition } from "../network/useChartingConditionSearch";
import {
  type CreateEpisodeOfCareParams,
  useCreateEpisodeOfCare,
} from "../network/useCreateEpisodeOfCare";
import { ChartingConditionSearch } from "./ChartingConditionSearch";
import { addEOCSchema } from "./schemas";

export const AddEpisodeOfCare = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const searchParams = useSearch({ from: "/patient/$patientId" });

  const encounterId = searchParams.encounterId as string; //  `Chart` is conditionally rendered by presence of encounterId

  const [isAddingEOC, setIsAddingEOC] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const createEOC = useCreateEpisodeOfCare({ encounterId });

  const form = useForm<z.infer<typeof addEOCSchema>>({
    resolver: zodResolver(addEOCSchema),
    defaultValues: {
      selectedConditionCode: "",
    },
  });

  const handleClick = () => {
    trackEvent(TRACKERS.ADD_EOC);
    setIsAddingEOC(true);
  };

  const handleConditionSelected = async (
    selectedCondition?: ChartingCondition,
  ) => {
    if (!selectedCondition) {
      toast.error("Could not match condition for submission, please reselect.");
      setIsAddingEOC(false);
      return;
    }

    let payloadData: CreateEpisodeOfCareParams;

    // If the condition already has an EoC, we need to pass the EoC id to the backend
    // to add a reference to that EoC for this mother encounter, creating a new child encounter
    if (selectedCondition?.existingEpisodeOfCareId) {
      payloadData = {
        patientId,
        motherEncounterId: encounterId,
        existingEpisodeOfCareId: selectedCondition.existingEpisodeOfCareId,
      };
      // If the condition does not have an existing EoC, we need to create a new EoC
    } else {
      payloadData = {
        patientId,
        motherEncounterId: encounterId,
        conditionCode: {
          coding: [
            {
              code: selectedCondition.conditionCode,
              system: selectedCondition.conditionSystem,
              display: selectedCondition.conditionDisplay,
            },
          ],
        },
      };
    }

    try {
      await createEOC.mutateAsync(payloadData);
      setIsAddingEOC(false);
    } catch (error) {
      toast.error("Failed to add EOC");
    }
  };

  useEffect(() => {
    if (isAddingEOC)
      ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [isAddingEOC]);

  if (isAddingEOC)
    return (
      <div className="border rounded-md px-3 py-2" ref={ref}>
        <form onSubmit={form.handleSubmit(() => undefined)}>
          <Controller
            control={form.control}
            name="selectedConditionCode"
            render={({ fieldState }) => (
              <Field className="flex-1" data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="selectedConditionCode">
                  New Condition
                </FieldLabel>
                <ChartingConditionSearch
                  aria-label="New Condition"
                  id="selectedConditionCode"
                  patientId={patientId}
                  motherEncounterId={encounterId}
                  isLoading={createEOC.isPending}
                  disabled={createEOC.isPending}
                  onConditionSelected={handleConditionSelected}
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />

          <div className="flex gap-2 w-fit mt-3 ml-auto">
            {!createEOC.isPending && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setIsAddingEOC(false)}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </div>
    );

  return (
    <Button size="xs" onClick={handleClick} className="w-fit h-fit">
      Add EOC
    </Button>
  );
};
